import { AddCircleOutline, Check, Close, Delete, Edit, FileDownload } from "@mui/icons-material";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	OutlinedInput,
	Slider,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import { GridActionsCellItem, GridColDef, GridRenderCellParams, GridRowParams, GridRowSelectionModel } from "@mui/x-data-grid";
import { useConfirmDialog } from "components/ConfirmDialog";
import Container from "components/Container";
import Loading from "components/Loading";
import Table from "components/Table";
import Main from "layouts/Main";
import React from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
	createOrganizationUser,
	exportChatHistory,
	getOrganizationData,
	getOrganizationUsers,
	suspendOrganizationUser,
	transferCredit,
	updateOrganizationUserInfo,
} from "services/api";
import GenerateKeyButton from "./components/GenerateKeyButton";
import { closeSnackbar } from "notistack";
import { AccountContext } from "services/account";
import { showSnackbar } from "services/snackbarHelper";

const OrganizationUserList = () => {
	const { id } = useParams();
	const { t } = useTranslation(["common"]);
	const { openDialog } = useConfirmDialog();
	const account = React.useContext(AccountContext);

	const [username, setUsername] = React.useState("");
	const [selectedUserId, setSelectedUserId] = React.useState(null);
	const [credit, setCredit] = React.useState(0);
	const [open, setOpen] = React.useState(false);
	const [rows, setRows] = React.useState([]);

	const [organization, setOrganization] = React.useState(null);

	const [rowSelectionModel, setRowSelectionModel] = React.useState<GridRowSelectionModel>([]);

	const [isLoading, setIsLoading] = React.useState(false);
	const columns: GridColDef[] = [
		{
			field: "userId",
			headerName: t("userId"),
			flex: 1,
		},
		{
			field: "username",
			headerName: t("username"),
			flex: 1,
		},
		// {
		// 	field: "createTime",
		// 	headerName: t("createTime"),
		// 	flex: 1,
		// 	valueFormatter: (value?: string) => {
		// 		return new Date(value).toLocaleString();
		// 	},
		// },
		{
			field: "suspended",
			headerName: t("activation"),
			flex: 1,
			renderCell: (params: GridRenderCellParams) => {
				return (
					<Switch
						checked={!params.value}
						onChange={(e) => updateSuspend(params.row.userId, !e.target.checked)}
						inputProps={{ "aria-label": "controlled" }}
					/>
				);
			},
		},
		{
			field: "paidCredit",
			headerName: t("paidCredit"),
			flex: 1,
		},
		{
			field: "useAdminCredit",
			headerName: t("useAdminCredit"),
			flex: 1,
			renderCell: (params: GridRenderCellParams) => {
				return (
					<Switch
						checked={params.value}
						onChange={(e) => updateUseAdminCredit(params.row.userId, e.target.checked)}
						inputProps={{ "aria-label": "controlled" }}
					/>
				);
			},
		},
		{
			field: "actions",
			type: "actions",
			getActions: (params: GridRowParams) => [
				<GenerateKeyButton
					organizationId={id}
					userId={params.row.userId}
				/>,
				<GridActionsCellItem
					icon={<Edit />}
					onClick={() => {
						setUsername(params.row.username);
						setSelectedUserId(params.row.userId);
						setOpen(true);
					}}
					label={t("edit")}
					showInMenu
				/>,
				<GridActionsCellItem
					icon={<FileDownload />}
					onClick={() => {
						exportChats([params.row.userId]);
					}}
					label={t("exportAllChatHistory")}
					showInMenu
				/>,
				// <GridActionsCellItem
				// 	icon={<Delete color="error" />}
				// 	onClick={() => handleDelete(id, params.row.userId)}
				// 	label={t("suspend")}
				// 	showInMenu
				// 	sx={{ color: (theme) => theme.palette.error.main }}
				// />,
			],
		},
	];

	const exportChats = (userIds: string[]) => {
		const snackId = showSnackbar(t("processing"));
		exportChatHistory(userIds).then((resp) => {
			console.log(resp);
			if (!resp || resp?.error) return;
			closeSnackbar(snackId);
			showSnackbar(t(resp.message, { ns: "validation" }), "success");
		});
	};
	const handleChangeCredit = (event: Event, newValue: number | number[]) => {
		if (typeof newValue === "number") {
			setCredit(newValue);
		}
	};

	const handleChange = (rows: any[]) => {
		setRows(rows);
	};

	const onDelete = (organizationId: string, userId: string) => {
		suspendOrganizationUser(organizationId, userId, "suspend").then((resp) => {
			console.log(resp);
			if (resp.info) {
				showSnackbar(t(resp.info, { ns: "validation" }), "success");
				updateUserList();
			}
		});
	};

	const handleDelete = (organizationId: string, userId: string) => {
		handleClose();
		openDialog(t("deleteOrganizationUser"), t("deleteOrganizationUserMessage"), () => onDelete(organizationId, userId));
	};

	const updateSuspend = (userId: string, suspend: boolean) => {
		suspendOrganizationUser(id, userId, suspend ? "suspend" : "reactivate").then((resp) => {
			console.log(resp);
			if (resp.info) {
				showSnackbar(t(resp.info, { ns: "validation" }), "success");
				updateUserList();
			}
		});
	};

	const updateUseAdminCredit = (userId: string, useAdminCredit: boolean) => {
		const snackbarId = showSnackbar(t("processing"));
		updateOrganizationUserInfo(id, userId, useAdminCredit).then(async (resp) => {
			if (resp.info) {
				await updateUserList();
				closeSnackbar(snackbarId);
				showSnackbar(t("updateSuccess", { ns: "validation" }), "success");
			}
		});
	};

	const updateUserList = () => {
		return getOrganizationUsers(id).then((resp) => {
			if (!resp || resp?.error) return;
			setRows(resp);
		});
	};
	const handleClose = () => {
		setOpen(false);
		setUsername("");
		setSelectedUserId(null);
		setCredit(0);
	};

	const handleUpdate = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const formData = new FormData(event.currentTarget);
		const formJson = Object.fromEntries((formData as any).entries());

		const userName: string = formJson.userName;
		if (selectedUserId) {
			transferCredit(id, selectedUserId, credit).then(async (resp) => {
				if (resp.info) {
					await updateUserList().finally(() => {
						handleClose();
						showSnackbar(t("updateSuccess", { ns: "validation" }), "success");
					});
				}
			});
		} else {
			createOrganizationUser(id, userName).then((resp) => {
				if (resp.info) {
					updateUserList().finally(() => handleClose());
				}
			});
		}
	};
	React.useEffect(() => {
		getOrganizationData(id).then((resp) => {
			if (!resp || resp?.error) return;
			setOrganization(resp);
		});
	}, []);
	React.useEffect(() => {
		updateUserList().finally(() => setIsLoading(false));
	}, []);

	React.useEffect(() => {
		console.log(rows);
	}, [rows]);

	return (
		<Main title={(organization?.organizationName ? organization?.organizationName + " | " : "") + t("userList")}>
			{isLoading ? (
				<Loading />
			) : (
				<Container
					maxWidth={1080}
					px={3}>
					<Box>
						<Box
							width={1}
							display="flex"
							justifyContent={"space-between"}
							mb={1}>
							<Box>
								{rowSelectionModel.length > 0 && (
									<Button
										variant="contained"
										onClick={() => exportChats(rowSelectionModel as string[])}
										startIcon={<FileDownload />}>
										{t("exportAllChatHistory")}
									</Button>
								)}
							</Box>

							<IconButton
								size="small"
								onClick={() => setOpen(true)}>
								<AddCircleOutline />
							</IconButton>
						</Box>
					</Box>
					<Table
						loading={isLoading}
						columns={columns}
						rows={rows}
						handleChange={handleChange}
						checkboxSelection
						onRowSelectionModelChange={(newRowSelectionModel) => {
							setRowSelectionModel(newRowSelectionModel);
						}}
						rowSelectionModel={rowSelectionModel}
						getRowId={(row) => row.userId}
					/>

					<Dialog
						open={open}
						onClose={handleClose}
						fullWidth
						maxWidth={"sm"}
						disableEscapeKeyDown
						slotProps={{
							paper: {
								component: "form",
								onSubmit: handleUpdate,
							},
						}}>
						<DialogTitle>{t(selectedUserId ? "editOrganizationUser" : "createOrganizationUser")}</DialogTitle>
						<DialogContent>
							{/* <DialogContentText>
								To subscribe to this website, please enter your email address here. We will send updates occasionally.
							</DialogContentText> */}
							<TextField
								autoFocus
								required
								margin="dense"
								id="userName"
								name="userName"
								label={t("username")}
								defaultValue={username}
								fullWidth
								variant="outlined"
								disabled={selectedUserId}
							/>
							{selectedUserId && !rows.find((row) => row.userId === selectedUserId).useAdminCredit && (
								<FormControl
									fullWidth
									sx={{ mt: 1 }}>
									<Typography>
										{t("remindCredit")}: {account.value.paidCredit - credit}
									</Typography>
									<Box
										mt={1}
										width={1}
										display="flex"
										flexDirection={"row"}
										gap={2}>
										<Slider
											value={credit}
											min={0}
											step={1}
											max={account.value.paidCredit}
											onChange={handleChangeCredit}
											valueLabelDisplay="auto"
										/>
										<OutlinedInput
											name="credit"
											value={credit}
											size="small"
											onChange={(e) => {
												const value = parseInt(e.target.value);

												if (!value) {
													return setCredit(null);
												}
												if (value > account.value.paidCredit) {
													return setCredit(account.value.paidCredit);
												}
												setCredit(value);
											}}
											inputProps={{
												min: 0,
												step: 1,
												max: account.value.paidCredit,
												type: "number",
											}}
											sx={{ minWidth: 65 }}
										/>
									</Box>
								</FormControl>
							)}
						</DialogContent>
						<DialogActions>
							<Button
								onClick={handleClose}
								color="inherit">
								{t("cancel")}
							</Button>
							<Button
								type="submit"
								variant="contained">
								{t("submit")}
							</Button>
						</DialogActions>
					</Dialog>
				</Container>
			)}
		</Main>
	);
};

export default OrganizationUserList;
